import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';

/**
 * ページ全体のコンテナ
 */
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

/**
 * ヘッダーセクション
 */
export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

/**
 * ナビゲーションエリア
 */
export const NavigationArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * アクションエリア
 */
export const ActionArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * タイムラインコンテナ
 */
export const TimelineContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

/**
 * タイムラインヘッダー
 */
export const TimelineHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
}));

/**
 * 医師名ラベル列
 */
export const DoctorNameColumn = styled(Box)(({ theme }) => ({
  width: 150,
  minWidth: 150,
  padding: theme.spacing(1),
  borderRight: `1px solid ${theme.palette.divider}`,
  fontWeight: theme.typography.fontWeightMedium,
  display: 'flex',
  alignItems: 'center',
}));

/**
 * 時間軸コンテナ
 */
export const TimeAxisContainer = styled(Box)(() => ({
  flex: 1,
  position: 'relative',
  // ラベルは絶対配置（シフトバーと同じ%スケール）のため高さを確保する
  minHeight: 28,
}));

/**
 * 時間ラベル
 */
export const TimeLabel = styled(Box)(({ theme }) => ({
  // シフトバーと同じ「(時刻-開始時刻)/総時間」の%位置に絶対配置する。
  // 旧実装の flex 等分（ラベル数で割る）はバーのスケールと一致せず
  // 目盛りと枠位置がズレる原因だった。
  position: 'absolute',
  top: 0,
  padding: theme.spacing(0.5, 0),
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
}));

/**
 * タイムライン行
 */
export const TimelineRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
  minHeight: 48,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * シフトエリア（タイムライン部分）
 */
export const ShiftArea = styled(Box)(() => ({
  flex: 1,
  position: 'relative',
  minHeight: 40,
}));

/**
 * シフトバー（通常枠）
 */
export const ShiftBarNormal = styled(Box)(({ theme }) => ({
  position: 'absolute',
  height: 32,
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: theme.palette.primary.main,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.contrastText,
  fontSize: '0.75rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: theme.spacing(0, 1),
  transition: 'opacity 0.2s',
  '&:hover': {
    opacity: 0.9,
  },
}));

/**
 * シフトバー（急募枠）
 */
export const ShiftBarUrgent = styled(ShiftBarNormal)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.error.contrastText,
}));


/**
 * 日付グループコンテナ
 * 同一日付の医師ごとの行をまとめる
 */
export const DateGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

/**
 * 日付グループ内の医師行
 */
export const DoctorRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: 48,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:not(:last-child)': {
    borderBottom: `1px dashed ${theme.palette.divider}`,
  },
}));

/**
 * 日付ラベル列（日付グループの先頭行用）
 */
export const DateLabelColumn = styled(Box)(({ theme }) => ({
  // ヘッダーの固定列（DoctorNameColumn: 150px）と同幅にしないと
  // シフトバーが時間軸より左へズレて表示される
  width: 150,
  minWidth: 150,
  padding: theme.spacing(1),
  borderRight: `1px solid ${theme.palette.divider}`,
  fontWeight: theme.typography.fontWeightMedium,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[50],
}));


/**
 * 医師行コンテナ（日付グループ内の医師行をまとめる）
 */
export const DoctorRowsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
}));

/**
 * 医師名列（日付グループ内の各行用）
 */
export const DoctorNameCell = styled(Box)(({ theme }) => ({
  width: 120,
  minWidth: 120,
  padding: theme.spacing(0.5, 1),
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

/**
 * 枠集計テーブルコンテナ
 */
export const SlotSummaryContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
}));

/**
 * エラーコンテナ
 */
export const ErrorContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/**
 * 日付ヘッダー
 */
export const DateHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  fontWeight: theme.typography.fontWeightMedium,
}));

/**
 * フィルターバッジ
 */
export const FilterBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: '50%',
  width: 18,
  height: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.7rem',
  marginLeft: theme.spacing(0.5),
}));

/**
 * 空のステート表示
 */
export const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
}));

/**
 * 時間グリッド線
 */
export const TimeGridLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: 1,
  backgroundColor: theme.palette.divider,
  pointerEvents: 'none',
}));
